import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Crown, LogOut } from "lucide-react";
import { toast } from "sonner";
import PickCard from "@/components/PickCard";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { useTimezone, tzAbbr, COMMON_TIMEZONES } from "@/lib/time";
import { useOddsFormat, ODDS_FORMATS } from "@/lib/odds";

export function VipPicksTab({ data, onUnlock }) {
  return (
    <div className="grid gap-6 md:grid-cols-2" data-testid="dashboard-vip-picks">
      {(data?.latest_vip_picks || []).map((p, i) => <PickCard key={p.id} pick={p} index={i} onUnlock={onUnlock} />)}
    </div>
  );
}

export function AnalysisTab({ data, onUnlock }) {
  const withAnalysis = useMemo(() => (data?.latest_vip_picks || []).filter((p) => p.analysis), [data]);
  return (
    <div className="grid gap-6 md:grid-cols-2" data-testid="dashboard-analysis">
      {withAnalysis.map((p, i) => <PickCard key={p.id} pick={p} index={i} onUnlock={onUnlock} />)}
    </div>
  );
}

export function ResultsTab({ data, chart }) {
  return (
    <div className="card-mp p-5 sm:p-6" data-testid="dashboard-results">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold uppercase tracking-wide text-white">Your Tracked Season</h3>
        <Link to="/results" data-testid="dashboard-full-results-link" className="font-mono text-xs uppercase tracking-wider text-[#00E676] hover:underline">
          Full ledger →
        </Link>
      </div>
      <div className="mt-4">{chart}</div>
      <div className="mt-4 space-y-3">
        {(data?.recent_results || []).map((p) => (
          <div key={p.id} className="flex items-center justify-between rounded-lg border border-[#1E2738] bg-[#0F131D] px-4 py-3">
            <span className="text-sm font-semibold text-slate-200">{p.player ? `${p.player} ${p.selection}` : p.selection}</span>
            <StatusBadge status={p.status} profit={p.profit} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function BankrollTab({ data }) {
  const net = data?.stats?.net_units ?? 0;
  const items = [
    { label: "Season Units", value: `${net > 0 ? "+" : ""}${net}U` },
    { label: "Our Reference", value: "1U = $100" },
    { label: "Suggested Unit Size", value: "1–2% of bankroll" },
  ];
  return (
    <div className="card-mp p-6 sm:p-8" data-testid="dashboard-bankroll">
      <h3 className="font-display text-lg font-bold uppercase tracking-wide text-white">Bankroll Education</h3>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {items.map((s) => (
          <div key={s.label} className="rounded-lg border border-[#1E2738] bg-[#0F131D] p-4">
            <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{s.label}</p>
            <p className="mt-1.5 font-mono text-lg font-bold text-[#00E676]">{s.value}</p>
          </div>
        ))}
      </div>
      <ul className="mt-6 space-y-2.5 text-sm leading-relaxed text-slate-400">
        <li>· <span className="text-slate-200">Understand Units:</span> Money Printer uses a standard reference of 1U = $100 for tracking our results. Your personal unit value can be different.</li>
        <li>· <span className="text-slate-200">Set Your Own Stake:</span> If your standard stake is $10, then 1U = $10. You don't need to match our $100 reference.</li>
        <li>· <span className="text-slate-200">Protect Your Bankroll:</span> Choose an affordable unit size, typically no more than 1–2% of your total bankroll.</li>
        <li>· <span className="text-slate-200">Never Chase Losses:</span> Stick to your predetermined unit size regardless of recent results.</li>
        <li>· <span className="text-slate-200">Track Performance:</span> Our results are measured in units so everyone can compare performance regardless of their individual stake size.</li>
        <li>· <span className="text-slate-200">No Guarantees:</span> Every pick carries risk. Past performance does not guarantee future returns.</li>
      </ul>
    </div>
  );
}

export function CommunityTab({ isVip, discordUrl, vipUrl }) {
  return (
    <div className="card-mp p-6 sm:p-8 text-center" data-testid="dashboard-community">
      <Users size={30} className="mx-auto text-[#00E676]" />
      <h3 className="mt-4 font-display text-xl font-bold uppercase text-white">The Community Lives On Discord</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-400">
        {isVip
          ? "Your VIP Discord role is assigned automatically by Whop. Use the link below to join the private server."
          : "The private Discord is for VIP members. Your role is granted automatically after checkout."}
      </p>
      {isVip && discordUrl && (
        <a href={discordUrl} target="_blank" rel="noopener noreferrer" data-testid="dashboard-discord-link" className="btn-primary mt-6">
          Join The VIP Discord
        </a>
      )}
      {isVip && !discordUrl && (
        <p className="mt-6 font-mono text-[10px] uppercase tracking-wider text-[#F59E0B]/80" data-testid="dashboard-discord-pending">Discord invite link will appear here once configured</p>
      )}
      {!isVip && (
        <a href={vipUrl} target={vipUrl?.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid="dashboard-community-upgrade" className="btn-primary mt-6">
          <Crown size={15} /> Join VIP
        </a>
      )}
    </div>
  );
}

export function AccountTab({ user, isVip, vipUrl, onLogout }) {
  const rows = [["Name", user.name], ["Email", user.email], ["Member Since", user.member_since]];
  return (
    <div className="space-y-6">
    <div className="card-mp p-6 sm:p-8" data-testid="dashboard-account">
      <h3 className="font-display text-lg font-bold uppercase tracking-wide text-white">Account</h3>
      <div className="mt-5 space-y-3 font-mono text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between border-b border-[#1E2738] pb-3"><span className="text-slate-500">{k}</span><span className="text-white">{v}</span></div>
        ))}
        <div className="flex justify-between border-b border-[#1E2738] pb-3">
          <span className="text-slate-500">Plan</span>
          <span className={isVip ? "font-bold text-[#F59E0B]" : "font-bold text-[#00E676]"}>{isVip ? "VIP" : "FREE"}</span>
        </div>
      </div>
      {!isVip && (
        <a href={vipUrl} target={vipUrl.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid="account-upgrade-btn" className="btn-primary mt-6 w-full">
          <Crown size={15} /> Upgrade To VIP
        </a>
      )}
      <button
        onClick={onLogout}
        data-testid="account-logout-btn"
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-[#EF4444]/40 px-4 py-3 font-display text-sm font-bold uppercase tracking-wider text-[#EF4444] transition-colors hover:bg-[#EF4444]/10"
      >
        <LogOut size={15} /> Log Out
      </button>
    </div>
    <PreferencesPanel />
    </div>
  );
}

export function PreferencesPanel() {
  const { tz, setTz, detected } = useTimezone();
  const { format } = useOddsFormat();
  const { savePreferences, isMember } = useAuth();
  const [saving, setSaving] = useState(false);
  const options = COMMON_TIMEZONES.includes(detected) ? COMMON_TIMEZONES : [detected, ...COMMON_TIMEZONES];
  const save = async (prefs) => {
    setSaving(true);
    try { await savePreferences(prefs); toast.success(isMember ? "Preference saved to your account" : "Preference saved on this device"); }
    catch { toast.error("Could not save preference"); }
    finally { setSaving(false); }
  };
  return (
    <div className="card-mp p-6 sm:p-8" data-testid="dashboard-preferences">
      <h3 className="font-display text-lg font-bold uppercase tracking-wide text-white">Display Preferences</h3>
      <p className="mt-1 text-xs text-slate-500">Applied everywhere — picks, dashboard, results. {isMember ? "Saved to your account." : "Saved on this device."}</p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Time zone</span>
          <select data-testid="pref-timezone-select" disabled={saving} value={tz} onChange={(e) => save({ timezone: e.target.value })} className="admin-input mt-1.5">
            {options.map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")} ({tzAbbr(z)}){z === detected ? " · detected" : ""}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Odds format</span>
          <select data-testid="pref-odds-select" disabled={saving} value={format} onChange={(e) => save({ odds_format: e.target.value })} className="admin-input mt-1.5">
            {ODDS_FORMATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>
      <p className="mt-3 font-mono text-[10px] text-slate-600">Example: 1.90 decimal = -111 American = 9/10 fractional. Profit, ROI and units are always calculated from the original decimal price.{tz !== detected && <button type="button" className="ml-2 text-[#00E676] hover:underline" onClick={() => { setTz(""); save({ timezone: detected }); }}>Use detected ({detected.replace(/_/g, " ")})</button>}</p>
    </div>
  );
}
